import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, PrimaryButton } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Airport, Trip } from '@shared/types';

export function HomePage() {
  const { user } = useAuth();
  const [airports, setAirports] = useState<Airport[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);

  useEffect(() => {
    api.airports().then((d) => setAirports(d.airports)).catch(() => undefined);
    api.trips().then((d) => setTrips(d.trips)).catch(() => undefined);
  }, []);

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brass">
          {user?.mode === 'guest' ? 'Guest session' : 'Household session'}
        </p>
        <h2 className="mt-1 font-display text-[30px] leading-none text-navy">
          Plan the hold, not the directory.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          Seeded for {user?.displayName ?? 'you'} on United-focused trips through EWR, PBI, RSW, and BNA. Recommendations cite the Jul 17 2026 playbook, the 2026-09-14 official location-list addendum, and your Benefit Wallet.
        </p>
        <div className="mt-4">
          <Link to="/trips/new">
            <PrimaryButton>New trip plan</PrimaryButton>
          </Link>
        </div>
      </Card>

      <Card>
        <h3 className="font-display text-2xl text-navy">Seeded airports</h3>
        <ChipRow className="mt-3">
          {airports.map((airport) => (
            <Link key={airport.iata} to={`/airports/${airport.iata}`}>
              <Chip tone={airport.hasAirlineLounge ? 'navy' : 'paper'}>{airport.iata}</Chip>
            </Link>
          ))}
        </ChipRow>
        <ul className="mt-4 space-y-3">
          {airports.map((airport) => (
            <li key={airport.id}>
              <Link to={`/airports/${airport.iata}`} className="block">
                <p className="text-sm font-semibold text-ink">
                  {airport.iata} · {airport.city}
                </p>
                <p className="text-[13px] leading-relaxed text-muted">{airport.playbookSummary}</p>
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-navy">Saved trips</h3>
          <Link to="/trips" className="text-[12px] font-semibold text-navy">
            All
          </Link>
        </div>
        {trips.length === 0 ? (
          <p className="mt-2 text-sm text-muted">None yet. Create a trip to get eligibility, reachability, and a booking timeline.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {trips.slice(0, 3).map((trip) => (
              <li key={trip.id}>
                <Link to={`/trips/${trip.id}`} className="flex justify-between text-sm">
                  <span className="font-semibold">
                    {trip.originIata} → {trip.destIata}
                  </span>
                  <span className="text-muted">{trip.airline}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
